import { useEffect, useId } from "react";
import { useLocation } from "react-router";
import { useAgentStore, type PageTour, type TourStop } from "@/stores/agent-store";

export type { TourStop };

// Register the mounted page's guided tour (/tour): a live-data summary + ordered stops, each
// pointing at a [data-assistant-anchor] element on the page. Same lifecycle contract as
// useAssistantContext — register on mount, replace when the composed strings change, clear on
// unmount — so the tour driver always finds a tour that matches what's actually on screen.
// Pass null while the page's data is still loading.
export function useAssistantTour(
  tour: { label: string; summary: string; stops: TourStop[] } | null | undefined,
): void {
  const id = useId();
  const { pathname } = useLocation();
  const setPageTour = useAgentStore((s) => s.setPageTour);
  const clearPageTour = useAgentStore((s) => s.clearPageTour);

  // Serialize for a stable effect dependency (same trick as use-assistant-context): the effect
  // re-parses `key`, so it only writes to the store when content or route actually changes.
  const key = tour ? JSON.stringify({ route: pathname, ...tour }) : "";

  useEffect(() => {
    if (key) setPageTour(id, JSON.parse(key) as PageTour);
    else clearPageTour(id);
    return () => clearPageTour(id);
  }, [id, key, setPageTour, clearPageTour]);
}
